<template>
  <Teleport to="body">
    <Transition name="offline-fade">
      <div
        v-if="offline.state.isOffline"
        class="offline-screen"
        dir="rtl"
        lang="ar"
        role="alert"
        aria-live="assertive"
      >
        <div class="offline-screen__content">
          <img
            class="offline-screen__logo"
            src="@/assets/images/logo_navbar.png"
            alt="Mr.Tarek Maths"
          />

          <div class="offline-screen__icon-wrap">
            <span class="offline-screen__ring offline-screen__ring--1"></span>
            <span class="offline-screen__ring offline-screen__ring--2"></span>
            <svg
              class="offline-screen__icon"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
              aria-hidden="true"
            >
              <path d="M16.72 11.06A10.94 10.94 0 0 1 19 12.55" />
              <path d="M5 12.55a10.94 10.94 0 0 1 5.17-2.39" />
              <path d="M10.71 5.05A16 16 0 0 1 22.58 9" />
              <path d="M1.42 9a15.91 15.91 0 0 1 4.7-2.88" />
              <path d="M8.53 16.11a6 6 0 0 1 6.95 0" />
              <line x1="12" y1="20" x2="12.01" y2="20" />
              <line x1="1" y1="1" x2="23" y2="23" />
            </svg>
          </div>

          <h1 class="offline-screen__title">لا يوجد اتصال بالإنترنت</h1>
          <p class="offline-screen__subtitle">
            تعذر الاتصال بالخادم.<br />
            يرجى التأكد من اتصال الإنترنت ثم إعادة المحاولة.
          </p>

          <div class="offline-screen__actions">
            <button
              type="button"
              class="offline-screen__btn offline-screen__btn--primary"
              :class="{ 'is-shaking': shake }"
              :disabled="offline.state.checking"
              @click="handleRetry"
            >
              <span v-if="offline.state.checking" class="offline-screen__spinner"></span>
              {{ offline.state.checking ? 'جارٍ التحقق...' : 'إعادة المحاولة' }}
            </button>

            <button
              v-if="showHomeButton"
              type="button"
              class="offline-screen__btn offline-screen__btn--outline"
              @click="handleGoHome"
            >
              العودة للرئيسية
            </button>
          </div>

          <p class="offline-screen__hint">تتم محاولة إعادة الاتصال تلقائيًا...</p>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script>
import OfflineManager, { offlineState } from '@/services/OfflineManager';

export default {
  name: 'OfflineScreen',
  data() {
    return {
      offline: { state: offlineState },
      shake: false,
    };
  },
  computed: {
    showHomeButton() {
      return offlineState.homeCached && this.$route.path !== '/';
    },
  },
  methods: {
    async handleRetry() {
      const restored = await OfflineManager.retry();
      if (!restored) {
        this.shake = true;
        setTimeout(() => { this.shake = false; }, 500);
      }
    },
    handleGoHome() {
      OfflineManager.goHome();
    },
  },
};
</script>

<style scoped>
.offline-screen {
  position: fixed;
  inset: 0;
  z-index: 999999;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem 1.25rem;
  background: linear-gradient(160deg, #ffffff 0%, #f2f8fb 55%, #e7f2f7 100%);
  text-align: center;
}

.offline-screen__content {
  display: flex;
  flex-direction: column;
  align-items: center;
  max-width: 420px;
  width: 100%;
}

.offline-screen__logo {
  max-width: 200px;
  width: 100%;
  height: auto;
  margin-bottom: 1.75rem;
  animation: offline-breathe 3.5s ease-in-out infinite;
}

.offline-screen__icon-wrap {
  position: relative;
  width: 96px;
  height: 96px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 1.5rem;
}

.offline-screen__icon {
  width: 48px;
  height: 48px;
  color: var(--primary-color, #2a8bb1);
  position: relative;
  z-index: 1;
}

.offline-screen__ring {
  position: absolute;
  inset: 0;
  border-radius: 50%;
  border: 2px solid var(--primary-color, #2a8bb1);
  opacity: 0;
  animation: offline-pulse 2.4s ease-out infinite;
}

.offline-screen__ring--2 {
  animation-delay: 1.2s;
}

.offline-screen__title {
  font-size: 1.6rem;
  font-weight: 700;
  color: #16323e;
  margin: 0 0 0.75rem;
}

.offline-screen__subtitle {
  font-size: 1rem;
  line-height: 1.8;
  color: #56717d;
  margin: 0 0 2rem;
}

.offline-screen__actions {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  width: 100%;
}

.offline-screen__btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  min-width: 170px;
  padding: 0.75rem 1.5rem;
  border-radius: 0.6rem;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.25s ease-in-out;
  border: 1px solid var(--primary-color, #2a8bb1);
}

.offline-screen__btn--primary {
  background-color: var(--primary-color, #2a8bb1);
  color: #fff;
}

.offline-screen__btn--primary:hover:not(:disabled) {
  background-color: transparent;
  color: var(--primary-color, #2a8bb1);
}

.offline-screen__btn--primary:disabled {
  opacity: 0.75;
  cursor: not-allowed;
}

.offline-screen__btn--outline {
  background-color: transparent;
  color: var(--primary-color, #2a8bb1);
}

.offline-screen__btn--outline:hover {
  background-color: var(--primary-color, #2a8bb1);
  color: #fff;
}

.offline-screen__btn.is-shaking {
  animation: offline-shake 0.5s ease-in-out;
}

.offline-screen__spinner {
  width: 16px;
  height: 16px;
  border-radius: 50%;
  border: 2px solid rgba(255, 255, 255, 0.5);
  border-top-color: #fff;
  animation: offline-spin 0.7s linear infinite;
}

.offline-screen__hint {
  margin: 1.25rem 0 0;
  font-size: 0.825rem;
  color: #8ba3ac;
}

/* Transitions */
.offline-fade-enter-active,
.offline-fade-leave-active {
  transition: opacity 0.35s ease;
}
.offline-fade-enter-from,
.offline-fade-leave-to {
  opacity: 0;
}

/* Animations */
@keyframes offline-pulse {
  0% { transform: scale(0.6); opacity: 0.55; }
  100% { transform: scale(1.9); opacity: 0; }
}

@keyframes offline-breathe {
  0%, 100% { transform: scale(1); opacity: 1; }
  50% { transform: scale(1.04); opacity: 0.92; }
}

@keyframes offline-spin {
  to { transform: rotate(360deg); }
}

@keyframes offline-shake {
  10%, 90% { transform: translateX(-2px); }
  20%, 80% { transform: translateX(3px); }
  30%, 50%, 70% { transform: translateX(-5px); }
  40%, 60% { transform: translateX(5px); }
}

@media (prefers-reduced-motion: reduce) {
  .offline-screen__logo,
  .offline-screen__ring,
  .offline-screen__spinner {
    animation: none;
  }
}
</style>
